import { useState, useEffect, useMemo } from "react";
import { useConfig } from "../providers/config_provider";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Timing Constants

const DEFAULT_ROTATION_INTERVAL = 6000;
const DEFAULT_ANIMATION_SPEED_OUT = 8;
const DEFAULT_ANIMATION_SPEED_IN = 12;

export function useAnimatedPlaceholder(
  query: string,
  rotationInterval: number = DEFAULT_ROTATION_INTERVAL,
  animationSpeedOut: number = DEFAULT_ANIMATION_SPEED_OUT,
  animationSpeedIn: number = DEFAULT_ANIMATION_SPEED_IN,
): string {
  const { suggestedQuestions } = useConfig();
  const questions = useMemo(() => suggestedQuestions.map((question) => question.text), [suggestedQuestions]);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Rotate the placeholder to a new suggested question every X seconds

  const [lastPlaceholderIndex, setLastPlaceholderIndex] = useState(-1);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    if (query.length) {
      // If there's a query, pause our rotation
      setLastPlaceholderIndex(-1);
      return;
    }

    const interval = setInterval(() => {
      setLastPlaceholderIndex(placeholderIndex);
      setPlaceholderIndex((prevIndex) => (prevIndex + 1) % questions.length);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [placeholderIndex, query.length, questions, rotationInterval]);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Get the placeholders we're transitioning between based on idx

  const lastPlaceholder = useMemo(() => {
    return lastPlaceholderIndex >= 0 ? questions[lastPlaceholderIndex] : undefined;
  }, [lastPlaceholderIndex, questions]);

  const placeholder = useMemo(() => {
    if (questions.length === 0 || query.length > 0) return "";
    return questions[placeholderIndex];
  }, [placeholderIndex, questions, query.length]);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Animate between placeholders

  const [animatedPlaceholder, setAnimatedPlaceholder] = useState(placeholder);

  useEffect(() => {
    let currentIndex = 0;
    let animatingOut = true;
    let animatingIn = false;
    let lastFrameTime = 0;
    let timeAccumulator = 0;
    let animationFrameId: number | null = null;

    const animationTick = () => {
      // Each animation frame, check if we've waited long enough to remove or insert a character
      // by comparing the time since our last action (timeAccumulator) to the animation speed
      const currentTime = performance.now();
      const deltaTime = currentTime - lastFrameTime;
      lastFrameTime = currentTime;
      timeAccumulator += deltaTime;

      if (animatingOut) {
        // Check if we've waited long enough to remove a character
        const intervalPassed = timeAccumulator >= animationSpeedOut;
        if (intervalPassed) {
          timeAccumulator -= animationSpeedOut;

          // Remove each character one by one from the last placeholder
          if (currentIndex > 0 && lastPlaceholder) {
            const newPlaceholder = lastPlaceholder.substring(0, currentIndex);
            setAnimatedPlaceholder(newPlaceholder);
            currentIndex--;
            animationTick(); // Invoke this immediately in case enough time has passed to advance the animation again
          } else {
            // Animation complete, we need to start animating in the new placeholder
            animatingOut = false;
            animatingIn = true;
            animationTick(); // Invoke this immediately in case enough time has passed to advance the animation again
          }
        } else {
          animationFrameId = requestAnimationFrame(animationTick);
        }
      } else if (animatingIn) {
        // Check if we've waited long enough to insert a character
        const intervalPassed = timeAccumulator >= animationSpeedIn;
        if (intervalPassed) {
          timeAccumulator -= animationSpeedIn;

          // Insert each character one by one into the new placeholder
          if (currentIndex <= placeholder.length) {
            const newPlaceholder = placeholder.substring(0, currentIndex);
            setAnimatedPlaceholder(newPlaceholder);
            currentIndex++;
            animationTick(); // Invoke this immediately in case enough time has passed to advance the animation again
          } else {
            // Animation complete, we're done
            animatingIn = false;
          }
        } else {
          animationFrameId = requestAnimationFrame(animationTick);
        }
      }
    };

    if (lastPlaceholder) {
      // Reset and start animation when placeholder changes
      currentIndex = lastPlaceholder.length - 1;
      animatingOut = true;
      animatingIn = false;
      lastFrameTime = performance.now();
      timeAccumulator = 0;
      animationFrameId = requestAnimationFrame(animationTick);
    } else {
      // On first load, set the placeholder directly
      setAnimatedPlaceholder(placeholder);
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [placeholder, lastPlaceholder, animationSpeedIn, animationSpeedOut]);

  return animatedPlaceholder;
}
